import React, { HTMLProps, useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@design-system';
import { InputField, OtpInput } from '@design-system/inputs';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import { LoginFormValidationSchema } from './helpers';
import { Form } from './components/Form';
import { IBaseAuthentication } from '@cincel/client';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useRequestOtp } from '@pages/AuthPages/pages/LoginPage/hooks';
import clsx from 'clsx';
import { logger } from '@helpers';
import { isCincelHost } from 'utils';
import { keyframes, css } from '@emotion/react';
import { AuthActionLink } from '@pages/AuthPages/components/AuthActionLink';
import { AuthSocialLogin } from '@pages/AuthPages/components/AuthSocialLogin';

const highlightAnimation = keyframes`
  0% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.1); filter: brightness(1.2); }
  100% { transform: scale(1); filter: brightness(1); }
`;

export interface ILoginFormProps
  extends Omit<HTMLProps<HTMLElement>, 'onSubmit'> {
  /**
   * Custom CSS tailwind styles.
   */
  className?: string;
  /**
   * On submit action handler.
   */
  onSubmit: (v: IBaseAuthentication & { otp?: string }) => void;
  /**
   * White label's primary color.
   */
  primaryColor: string;
  /**
   * Auth0 login handler.
   */
  onAuth0Login?: (conn: 'google-oauth2' | 'windowslive') => void;
  /**
   * Auth0 loading state.
   */
  isAuth0Loading?: boolean;
  /**
   * Pre-filled email from external sources (e.g. query params).
   */
  defaultEmail?: string;
}

enum LoginStep {
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  OTP = 'OTP',
}

export const LoginFormBase: React.FC<ILoginFormProps> = (props): JSX.Element => {
  const { className, onSubmit, primaryColor, onAuth0Login, isAuth0Loading, defaultEmail, ...rest } = props;
  const [step, setStep] = useState<LoginStep>(LoginStep.EMAIL);
  const { mutateAsync: requestOtp, isLoading: isRequestingOtp } = useRequestOtp();
  const { control, formState, handleSubmit, watch, trigger } = useForm<IBaseAuthentication & { otp?: string }>({
    defaultValues: { email: defaultEmail || '', password: '', otp: '' },
    mode: 'onTouched',
    resolver: yupResolver(LoginFormValidationSchema),
  });

  const { t } = useTranslation();
  const [currentEmail, currentPassword] = watch(['email', 'password']);
  const [hasManuallyGoBack, setHasManuallyGoBack] = useState(false);

  // Lockout / Hint states
  const [failedPassword, setFailedPassword] = useState('');
  const [isLockedByError, setIsLockedByError] = useState(false);
  const [showForgotHint, setShowForgotHint] = useState(false);

  // Reset lockout if user changes the password
  useEffect(() => {
    if (currentPassword !== failedPassword) {
      setIsLockedByError(false);
    }
  }, [currentPassword, failedPassword]);

  // OTP Timer state
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Calculate remaining seconds from expiration time
  const updateRemainingTime = useCallback(() => {
    if (!expiresAt) {
      setRemainingSeconds(0);
      return;
    }
    const remaining = Math.max(
      0,
      Math.floor((expiresAt.getTime() - Date.now()) / 1000),
    );
    setRemainingSeconds(remaining);
  }, [expiresAt]);

  // Update countdown every second
  useEffect(() => {
    if (!expiresAt) return;

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, updateRemainingTime]);

  // Format remaining time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    logger.log('=== LoginForm MOUNTED ===');
    return () => logger.log('=== LoginForm UNMOUNTED ===');
  }, []);

  // Stable Autofill Detection
  useEffect(() => {
    if (step === LoginStep.EMAIL && currentEmail && currentPassword && !hasManuallyGoBack) {
      logger.log('Autofill detected. Sync switch to PASSWORD step.');
      setStep(LoginStep.PASSWORD);
    }
  }, [currentEmail, currentPassword, step, hasManuallyGoBack]);

  const goToPassword = async () => {
    const isEmailValid = await trigger('email');
    if (isEmailValid) {
      setStep(LoginStep.PASSWORD);
      setHasManuallyGoBack(false);
    }
  };

  const handleGoBackToEmail = () => {
    setHasManuallyGoBack(true);
    setStep(LoginStep.EMAIL);
  };

  const goToOtp = async () => {
    const isEmailValid = await trigger('email');
    if (isEmailValid) {
      const previousStep = step;
      // Transition immediately to give feedback
      setStep(LoginStep.OTP);
      setHasManuallyGoBack(false);
      try {
        const response = await requestOtp(currentEmail);
        if (response?.otp_expires_at) {
          const newExpiresAt = new Date(response.otp_expires_at);
          setExpiresAt(newExpiresAt);
          const remaining = Math.max(
            0,
            Math.floor((newExpiresAt.getTime() - Date.now()) / 1000),
          );
          setRemainingSeconds(remaining);
        }
      } catch (error) {
        // Go back if it fails
        setStep(previousStep);
      }
    }
  };

  const handleResendOtp = async () => {
    if (remainingSeconds > 0 || isRequestingOtp) return;
    try {
      const response = await requestOtp(currentEmail);
      if (response?.otp_expires_at) {
        const newExpiresAt = new Date(response.otp_expires_at);
        setExpiresAt(newExpiresAt);
        const remaining = Math.max(
          0,
          Math.floor((newExpiresAt.getTime() - Date.now()) / 1000),
        );
        setRemainingSeconds(remaining);
      }
    } catch (error) {
      // Error is handled by global toast usually
    }
  };

  const [isManualPasswordEntry, setIsManualPasswordEntry] = useState(false);

  const isEmailStep = step === LoginStep.EMAIL;
  const isPasswordStep = step === LoginStep.PASSWORD;
  const isOtpStep = step === LoginStep.OTP;

  const handleFormSubmit = async (data: IBaseAuthentication & { otp?: string }) => {
    if (isEmailStep) {
      goToOtp();
      return;
    }

    if (isPasswordStep) {
      try {
        await onSubmit(data);
        setShowForgotHint(false);
      } catch (error) {
        setFailedPassword(data.password);
        setIsLockedByError(true);
        setShowForgotHint(true);

        // Reactivate button after 5 seconds even if password doesn't change
        setTimeout(() => {
          setIsLockedByError(false);
        }, 5000);
      }
      return;
    }

    // 3. Step 2 - OTP
    if (isOtpStep) {
      onSubmit({
        email: data.email,
        password: data.otp as string,
      });
    }
  };

  const CustomLink = Link as any;

  return (
    <Form className={clsx("flex flex-col items-center", className)} onSubmit={handleSubmit(handleFormSubmit)} {...rest}>
      {/* 1. EMAIL FIELD - Behavior changes per step */}
      <InputField
        className="w-[300px] md:w-[340px] mb-2"
        control={control}
        name="email"
        placeholder={t(translations.INPUTS.EMAIL)}
        size="md"
        primaryColor={primaryColor}
        type="email"
        isDisabled={isOtpStep || isRequestingOtp} // Email is locked only in OTP step or while sending
        hideError
      />

      {/* 2. DYNAMIC AREA */}
      <div className="w-full flex flex-col items-center">

        {/* STEP 1: INITIAL STATE (EMAIL) */}
        <div className={clsx(
          "w-full flex flex-col items-center transition-all duration-300 overflow-hidden p-1",
          isEmailStep ? "max-h-[300px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        )}>
          <Button
            className="w-[295px] md:w-[335px] ml-[5px] mt-2 mb-4 whitespace-nowrap"
            color={primaryColor}
            isDisabled={!currentEmail || isRequestingOtp || !formState.isValid}
            isLoading={isRequestingOtp}
            size="sm"
            type="submit"
          >
            {t(translations.COMMON.CONTINUE, 'Continuar')}
          </Button>
        </div>

        {/* STEP 2: PASSWORD FALLBACK */}
        <div className={clsx(
          "w-full flex flex-col items-center transition-all duration-500 overflow-hidden p-1",
          isPasswordStep ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        )}>
          <InputField
            className="mb-8 w-[300px] md:w-[340px]"
            control={control}
            name="password"
            placeholder={t(translations.INPUTS.PASSWORD)}
            size="md"
            primaryColor={primaryColor}
            type="password"
          />

          <div className="flex gap-8 w-full mt-2 mb-4 items-center justify-center">
            <button
              type="button"
              onClick={goToOtp}
              disabled={isRequestingOtp}
              className="text-sm font-bold disabled:opacity-50 whitespace-nowrap"
              style={{ color: primaryColor }}
              css={showForgotHint ? css`animation: ${highlightAnimation} 1s infinite;` : undefined}
            >
              {isRequestingOtp
                ? t(translations.AUTH_PAGES.LOGIN_SENDING, 'Enviando...')
                : t(translations.AUTH_PAGES.LOGIN_FORGOT_PASSWORD, '¿Olvidaste tu contraseña?')
              }
            </button>
            <Button
              className="px-8 whitespace-nowrap"
              color={primaryColor}
              isDisabled={formState.isSubmitting || (isLockedByError && currentPassword === failedPassword)}
              isLoading={formState.isSubmitting}
              size="sm"
              type="submit"
            >
              {t(translations.COMMON.LOGIN)}
            </Button>
          </div>
        </div>

        {/* STEP 3: OTP VERIFICATION */}
        <div className={clsx(
          "w-full flex flex-col items-center transition-all duration-500 overflow-hidden p-1",
          isOtpStep ? "max-h-[500px] opacity-100 mt-2" : "max-h-0 opacity-0 pointer-events-none"
        )}>
          <p className="text-sm text-gray-400 mb-6 text-center whitespace-nowrap">
            {isRequestingOtp
              ? t(translations.AUTH_PAGES.LOGIN_SENDING, 'Enviando...')
              : t(translations.AUTH_PAGES.LOGIN_OTP_INSTRUCTIONS, 'Ingresa el código de 6 dígitos enviado a tu correo')
            }
          </p>

          <Controller
            control={control}
            name="otp"
            render={({ field }) => (
              <OtpInput
                value={field.value as string}
                onChange={field.onChange}
                onComplete={(val) => {
                  field.onChange(val);
                  handleSubmit(handleFormSubmit)();
                }}
                primaryColor={primaryColor}
                isDisabled={formState.isSubmitting || isRequestingOtp}
                autoFocus={isOtpStep}
              />
            )}
          />

          <div className="flex gap-8 w-full mt-8 mb-4 items-center justify-center">
            <button
              type="button"
              onClick={() => setStep(LoginStep.PASSWORD)}
              className="text-sm font-bold whitespace-nowrap"
              style={{ color: primaryColor }}
            >
              {t(translations.AUTH_PAGES.LOGIN_USE_PASSWORD, 'Prefiero usar mi contraseña')}
            </button>
            <Button
              className="px-8 whitespace-nowrap"
              color={primaryColor}
              isDisabled={formState.isSubmitting || (watch('otp')?.length !== 6)}
              isLoading={formState.isSubmitting}
              size="sm"
              type="submit"
            >
              {t(translations.COMMON.VALIDATE)}
            </Button>
          </div>

        </div>

        {/* SECONDARY OPTIONS (Common Bottom) */}
        {isCincelHost() && isEmailStep && onAuth0Login && (
          <AuthSocialLogin
            isDisabled={isAuth0Loading}
            onClick={onAuth0Login}
            separatorLabel={t('common.or', 'o')}
          />
        )}

        {/* FOOTER ACTIONS SLOT */}
        {(isEmailStep || isPasswordStep) && (
          <AuthActionLink
            label={t(translations.AUTH_PAGES.LOGIN_NO_ACCOUNT)}
            actionText={t(translations.AUTH_PAGES.LOGIN_REGISTER_FREE)}
            to="/signup"
          />
        )}


        {isOtpStep && (
          <AuthActionLink
            label={t('auth.noCode', '¿No llegó?')}
            actionText={isRequestingOtp
              ? t(translations.AUTH_PAGES.LOGIN_SENDING, 'Enviando...')
              : remainingSeconds > 0
                ? `${t(translations.AUTH_PAGES.REQUEST_NEW_CODE, 'Solicitar código')} (${formatTime(remainingSeconds)})`
                : t(translations.AUTH_PAGES.REQUEST_NEW_CODE, 'Solicitar código')
            }
            onClick={handleResendOtp}
            isDisabled={remainingSeconds > 0 || isRequestingOtp}
          />
        )}
      </div>
    </Form>
  );
};

export const LoginForm = React.memo(LoginFormBase);
